import React from 'react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { getWeatherDetails } from '../utils/weatherHelpers';

const WeatherSection = ({ title, date, data, delay, headerClassName, link, buttonText, imageUrl }) => {
  if (!data && !link) return null;
  const weather = data ? getWeatherDetails(data.weatherCode) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className="bg-white rounded-2xl shadow-lg border border-gray-100 flex flex-col overflow-hidden"
    >
      {imageUrl && (
        <div className="w-full aspect-video overflow-hidden bg-slate-200">
          <img 
            src={imageUrl} 
            alt={title} 
            className="w-full h-full object-contain transition-transform duration-700 hover:scale-105" 
          />
        </div>
      )}
      <div className={`p-6 ${headerClassName}`}>
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-black">{title}</h2>
            {date && (
              <p className="text-sm font-bold opacity-90 mt-1">
                {format(new Date(date), 'EEEE, MMMM do, yyyy')}
              </p>
            )}
          </div>
          <div className="p-2 rounded-full bg-white/20 backdrop-blur-sm">
            <SafeIcon name={weather ? weather.icon : "Cloud"} className="text-3xl text-white" />
          </div>
        </div>
      </div>
      <div className="p-8">
        {link ? (
          <div className="flex flex-col gap-4">
            <a 
              href={link} 
              target="_blank" 
              rel="noreferrer" 
              className="flex items-center justify-center gap-3 w-full py-6 px-6 bg-slate-900 hover:bg-black text-white rounded-2xl transition-all shadow-xl active:scale-[0.97] text-2xl font-black text-center"
            >
              <span>{buttonText}</span>
              <SafeIcon name="ExternalLink" className="text-xl" />
            </a>
            {/* Attribution footer removed as requested */}
          </div>
        ) : (
          <>
            <div className="flex flex-col justify-center">
              <div className="flex flex-col mb-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-black text-slate-400 uppercase tracking-wider">High</span>
                  <span className="text-7xl font-black text-slate-900">{Math.round(data.maxTemp)}°C</span>
                </div>
                <div className="flex items-baseline gap-2 -mt-2">
                  <span className="text-sm font-black text-slate-400 uppercase tracking-wider">Low</span>
                  <span className="text-4xl font-bold text-slate-500">{Math.round(data.minTemp)}°C</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${weather.color.replace('text', 'bg')}`}></div>
                <p className="text-2xl font-bold text-slate-700 capitalize">{weather.label}</p>
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-2 gap-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <SafeIcon name="CloudRain" className="text-blue-500 text-xl" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase">Precipitation</p>
                  <span className="text-xl text-slate-600 font-black">{data.precipProb}%</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-50 rounded-lg">
                  <SafeIcon name="Wind" className="text-slate-500 text-xl" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase">Max Wind</p>
                  <span className="text-xl text-slate-600 font-black">{Math.round(data.windSpeed)} km/h</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default WeatherSection;